import type {
  PatchAdminV1StoresStoreIdProductsIdBody,
  StoreProduct,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, toZonedInput } from '@nucleus-commerce/core';

import { fromMetadata, toMetadata } from '@/features/products';
import { fromSeoOverride, toSeoOverride } from '@/features/seo';

import type {
  StoreProductFormInput,
  StoreProductFormOutput,
} from '../schemas/store-product-form-schema';

/**
 * Form ↔ API for store overrides. The form holds blanks for "inherit"; the PATCH sends null for
 * those so a cleared field falls back to the master value, and every override key is sent so
 * the server state mirrors the form exactly. Schedule times are edited on the store's clock.
 */

const orNull = (v: string): string | null => (v === '' ? null : v);

/** Override opens on the master's text: left as it was, the store still follows the master. */
const override = (v: string, master: string | null | undefined): string | null =>
  v === (master ?? '').trim() ? null : orNull(v);

export function fromStoreProduct(row: StoreProduct, timeZone: string): StoreProductFormInput {
  return {
    status: row.status,
    handle: row.handle,
    title: row.title ?? '',
    subtitle: row.subtitle ?? '',
    descriptionHtml: row.descriptionHtml ?? '',
    tags: row.tags ?? [],
    seo: fromSeoOverride(row.seo ?? undefined),
    featuredMediaId: row.featuredMediaId,
    metadata: fromMetadata(row.metadata),
    position: row.position === null ? '' : String(row.position),
    publishAt: toZonedInput(row.publishAt, timeZone),
    unpublishAt: toZonedInput(row.unpublishAt, timeZone),
  };
}

/**
 * A schedule time as the PATCH wants it, or undefined when the form leaves it as it was: the
 * backend refuses a time that is not in the future, and a time loaded a while ago may have passed.
 */
function scheduleChange(
  value: string,
  saved: string | null,
  timeZone: string,
): string | null | undefined {
  if (value === toZonedInput(saved, timeZone)) return undefined;
  return value === '' ? null : fromZonedInput(value, timeZone);
}

/**
 * `existing` is the row the form was opened from: SEO keys this form does not edit (none today,
 * but the shape may grow) are carried over so a save never drops them, and unchanged schedule
 * times are left out.
 */
export function toStoreProductUpdate(
  values: StoreProductFormOutput,
  existing: Pick<StoreProduct, 'seo' | 'publishAt' | 'unpublishAt' | 'product'>,
  timeZone: string,
): PatchAdminV1StoresStoreIdProductsIdBody {
  const seo = toSeoOverride(values.seo);
  const publishAt = scheduleChange(values.publishAt, existing.publishAt, timeZone);
  const unpublishAt = scheduleChange(values.unpublishAt, existing.unpublishAt, timeZone);
  return {
    status: values.status,
    handle: values.handle,
    title: override(values.title, existing.product.title),
    subtitle: override(values.subtitle, existing.product.subtitle),
    descriptionHtml: override(values.descriptionHtml, existing.product.descriptionHtml),
    tags: values.tags.length > 0 ? values.tags : null,
    seo: seo ? { ...existing.seo, ...seo } : null,
    featuredMediaId: values.featuredMediaId,
    metadata: toMetadata(values.metadata),
    position: values.position === '' ? null : Number(values.position),
    ...(publishAt !== undefined ? { publishAt } : {}),
    ...(unpublishAt !== undefined ? { unpublishAt } : {}),
  };
}

/** What shoppers see: the override when set, the master value otherwise. */
export function displayTitle(row: StoreProduct): string {
  return row.title ?? row.product.title;
}

/** The pending scheduled change shown on lists: the earlier of publish and unpublish. */
export function nextScheduledChange(
  row: Pick<StoreProduct, 'publishAt' | 'unpublishAt'>,
): { kind: 'publish' | 'unpublish'; at: string } | null {
  const changes = [
    row.publishAt ? { kind: 'publish' as const, at: row.publishAt } : null,
    row.unpublishAt ? { kind: 'unpublish' as const, at: row.unpublishAt } : null,
  ].filter((change) => change !== null);
  changes.sort((a, b) => a.at.localeCompare(b.at));
  return changes[0] ?? null;
}
